import store from "@/store";
import router, { BankingRouteInterface, BankingRouteMeta } from "@/router";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import StorageService from "@/core/services/StorageService";
import {
  getDashboardRoute,
  isUserRoleIn,
} from "@/core/services/UserRoleService";
import { UserRole } from "@/core/services/api/enums/UserRole";
import { RouteLocationNormalizedLoaded } from "vue-router";
import * as process from "process";
import { includeScript } from "@/core/helpers/functions";
import { LiveChatType } from "@/interfaces/common/LiveChatType";
import { siteName } from "@/core/helpers/config";

type Namespace = "common" | "manager" | "admin";
type PromiseResult = object | string | number | void | null;

const isUserAuthenticated = (): boolean => store.getters.isUserAuthenticated;
const isIndividualOrHas2FA = (): boolean => store.getters.isIndividualOrHas2FA;
const isCompanyWhiteLabelEnabled = (): boolean =>
  store.getters.isCompanyWhiteLabelEnabled;
const isCompanyEnabled = (): boolean => store.getters.isUserCompanyEnabled;
const hasAffiliates = (): boolean => store.getters.hasAffiliates;
const isManager = (): boolean => store.getters.isManager;
const isAdmin = (): boolean => store.getters.isAdmin;
const isThemeIbanflex = (): boolean => store.getters.isThemeIbanflex;
const companyCryptoEnabled = (): boolean => {
  return store.getters.isCompanyFeatureCryptoEnabled;
};
const companyPortalsEnabled = (): boolean => {
  return store.getters.isCompanyFeaturePortalsEnabled;
};

const isUserKycRequired = (): boolean => {
  return (
    isUserAuthenticated() && store.getters.isUserComplianceTrackingRequired
  );
};
const isUserKycPreparing = (): boolean => {
  return (
    isUserAuthenticated() && store.getters.isUserComplianceTrackingPreparing
  );
};

enum TtlSeconds {
  MINUTE = 60 * 1000,
  FIVE_MINUTES = 5 * TtlSeconds.MINUTE,
  TEN_MINUTES = 10 * TtlSeconds.MINUTE,
  THIRTY_MINUTES = 30 * TtlSeconds.MINUTE,
  HOUR = 60 * TtlSeconds.MINUTE,
  DAY = 24 * TtlSeconds.HOUR,
}

export interface BootstrapParams {
  route: RouteLocationNormalizedLoaded & BankingRouteInterface;
  position: BootstrapPosition;
}

export enum BootstrapPosition {
  BEFORE_ROUTE = "before_route",
  AFTER_ROUTE = "after_route",
}

export interface BootstrapDataClause {
  name: string;
  namespace: Namespace;
  ttlSeconds: TtlSeconds | number;
  action: Actions | string;
  position: BootstrapPosition;
  isAllowed: (params: BootstrapParams) => boolean;
  forceHandler?: (params: BootstrapParams) => boolean;
  handler: (params: BootstrapParams) => Promise<PromiseResult>;
}

export interface BootstrapActionClause {
  name: string;
  namespace: Namespace;
  position: BootstrapPosition;
  description?: string;
  isAllowed: (params: BootstrapParams) => boolean;
  handler: (params: BootstrapParams) => void | Promise<PromiseResult>;
}

export const BootstrapActionsConfig: BootstrapActionClause[] = [
  {
    name: "loader-show",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: () => true,
    handler: async () => {
      return store.dispatch(Actions.THEME_BODY_CLASSNAME_ADD, "page-loading");
    },
  },
  {
    name: "scroll-to-top",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams) => !params.route.hash,
    handler: () => {
      setTimeout(() => window.scrollTo(0, 0), 100);
    },
  },
  {
    name: "check-release-version",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params) =>
      store.getters.getUser &&
      params.route.name !== "iframe-widget.companies.profile" &&
      "VUE_APP_RELEASE" in process.env &&
      !!process.env.VUE_APP_RELEASE,
    handler: () => {
      const latestRelease = window.localStorage.getItem("release_version");
      if (latestRelease !== process.env.VUE_APP_RELEASE) {
        console.log(
          "new release detected",
          latestRelease,
          process.env.VUE_APP_RELEASE
        );

        return store.dispatch(Actions.COMMON_AUTH_LOGOUT);
      }
    },
  },
  // {
  //   name: Mutations.THEME_LAYOUT_CONFIG_RESET,
  //   namespace: "common",
  //   position: BootstrapPosition.BEFORE_ROUTE,
  //   isAllowed: () => true,
  //   handler: () => {
  //     return store.commit(Mutations.THEME_LAYOUT_CONFIG_RESET);
  //   },
  // },
  {
    name: Actions.THEME_BODY_CLASSNAME_ADD,
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: () => true,
    handler: async () => {
      return await store.dispatch(
        Actions.THEME_BODY_CLASSNAME_ADD,
        store.getters.theme
      );
    },
  },
  {
    name: Actions.COMMON_ZOHO_APP_AUTHENTICATE,
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams) =>
      params.route.name === "iframe-widget.companies.profile",
    handler: async () => {
      return await store.dispatch(Actions.COMMON_ZOHO_APP_AUTHENTICATE);
    },
  },
  {
    name: "setting-title-and-breadcrumb",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: () => true,
    handler: async (params) => {
      const pageBreadcrumbPath = params.route.matched
        .map((route) => route.meta.title)
        .filter((title) => title);
      pageBreadcrumbPath.pop();

      let title = params.route.meta.title;
      if (typeof title === "function") {
        title = title(params.route);
      }

      store.commit(Mutations.THEME_BREADCRUMB_ACTION_SET, {
        title,
        pageBreadcrumbPath,
      });
    },
  },
  {
    name: "reset-system-overview-interval",
    namespace: "admin",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams) => {
      return isAdmin() && "admin.system.overview" !== params.route.name;
    },
    handler: async () => {
      return store.commit(Mutations.ADMIN_SYSTEM_OVERVIEW_SET_INTERVAL);
    },
  },
  {
    name: Actions.COMMON_AUTH_VERIFY,
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: () => true,
    handler: async () => {
      return await store
        .dispatch(Actions.COMMON_AUTH_VERIFY)
        .catch(console.error);
    },
  },
  {
    name: "check-for-authorization",
    description: "Check for Authorization. Redirects to login page if not.",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return !!params.route.meta?.auth && !isUserAuthenticated();
    },
    handler: async (params: BootstrapParams) => {
      StorageService.saveToStorage("redirect_after_login", params.route);
      store.commit(Mutations.COMMON_AUTH_PURGE);

      console.log("redirecting to login", params);

      return router.push({ name: "login" });
    },
  },
  {
    name: "logout on login page",
    description: "Logout on login page if user is authenticated",
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return (
        isUserAuthenticated() &&
        typeof params.route.name === "string" &&
        "login" === params.route.name
      );
    },
    handler: async () => {
      console.info("logout on login page");

      return store.dispatch(Actions.COMMON_AUTH_LOGOUT, {
        withRedirect: false,
      });
    },
  },
  {
    name: "redirect-to-portal-services",
    description:
      "If portal services are enabled - do redirect from Create Withdraw > Crypto to Portal Services",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return (
        params.route.name === "manager.transactions.create" &&
        params.route.params.method === "crypto" &&
        isThemeIbanflex() &&
        isUserAuthenticated() &&
        companyPortalsEnabled()
      );
    },
    handler: async (params: BootstrapParams) => {
      console.log("redirecting to portals", params);

      return router.push({
        name: "manager.portal-services",
        params: { portal_service: store.getters.getPortalServiceFirst.value },
      });
    },
  },
  {
    name: "check-for-company-enabled",
    description: "Check for Company Enabled. Redirects to profile if not.",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return (
        !store.getters.isUserCompanyEnabled &&
        isManager() &&
        !store.getters.isUserComplianceTrackingRequired &&
        "manager.profile" !== params.route.name
      );
    },
    handler: async () => {
      return router.push({ name: "manager.profile" });
    },
  },
  {
    name: "check-for-required-verifications",
    description:
      "Check if verifications required. Redirects to verifications page if not on it yet.",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return (
        typeof params.route.name === "string" &&
        !["profile.verifications", "flexpsp-quick-login", "login"].includes(
          params.route.name
        ) &&
        (isUserKycRequired() || isUserKycPreparing())
      );
    },
    handler: async (params: BootstrapParams) => {
      console.log("redirecting to verifications", params);

      return router.push({ name: "profile.verifications" });
    },
  },
  {
    name: "check-if-verifications-not-required",
    description:
      "Check if verifications not required. Redirects to dashboard if not required.",
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      return (
        "profile.verifications" === params.route.name &&
        !isUserKycRequired() &&
        !isUserKycPreparing()
      );
    },
    handler: async (params: BootstrapParams) => {
      console.log(
        "redirecting to dashboard. check-if-verifications-not-required",
        params
      );

      return router.push(getDashboardRoute());
    },
  },
  {
    name: "check-for-guest-if-authenticated",
    description:
      "Check if user already authenticated. Redirects to dashboard if yes.",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      const route = params.route;
      if (!isUserAuthenticated()) {
        return false;
      }
      if (false === route.meta.auth) {
        return true;
      }

      return (
        "roles" in route.meta && !isUserRoleIn(route.meta.roles as UserRole[])
      );
    },
    handler: async (params) => {
      console.log("redirecting to dashboard", params);

      return router.push(getDashboardRoute());
    },
  },
  {
    name: "check-for-user-allowed-visit-route",
    description:
      "Check if user is allowed to visit route. Redirects to dashboard if not.",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      const route = params.route;
      const routeMeta = route.meta as BankingRouteMeta;
      if (
        !("is_allowed" in routeMeta) ||
        typeof routeMeta.is_allowed !== "function"
      ) {
        return false;
      }

      return !routeMeta.is_allowed(route);
    },
    handler: async (params) => {
      console.log("redirecting to dashboard", params);

      return router.push(getDashboardRoute());
    },
  },
  {
    name: Actions.COMMON_WEBSOCKETS_INITIATE,
    description: "Initiate websocket connection.",
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: (): boolean => !store.getters.isWsEchoSet,
    handler: async () => {
      await store.dispatch(Actions.COMMON_WEBSOCKETS_INITIATE);
    },
  },

  {
    name: Actions.COMMON_AUTH_EXPIRES_REMINDER_SET,
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: (): boolean =>
      isUserAuthenticated() && !store.getters.getAuthExpiresInterval,
    handler: (): Promise<PromiseResult> => {
      console.log("setting interval for auth reminder");

      return store.dispatch(Actions.COMMON_AUTH_EXPIRES_REMINDER_SET);
    },
  },
  {
    name: "loader-hide",
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: () => true,
    handler: async () => {
      return store.dispatch(
        Actions.THEME_BODY_CLASSNAME_REMOVE,
        "page-loading"
      );
    },
  },
  {
    name: "set-title",
    namespace: "common",
    position: BootstrapPosition.AFTER_ROUTE,
    isAllowed: () => true,
    handler: async (params) => {
      const assigner = () => {
        const route = params.route;
        if (route.meta?.title) {
          let title = route.meta?.title;
          if (typeof title === "function") {
            title = title(route);
          }
          document.title = `${siteName.value}: ${title}`;
        } else {
          document.title = `${siteName.value}`;
        }
      };
      assigner();

      setTimeout(assigner, 500);
    },
  },
  {
    name: "INCLUDE_SALES_IQ_SCRIPT",
    namespace: "common",
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: () => {
      if (document.getElementById("zsiqscript")) {
        return false;
      }

      return (
        LiveChatType.SALES_IQ === store.getters.getLiveChatType &&
        isUserAuthenticated() &&
        store.getters.getUserLiveChatSettings &&
        store.getters.getLiveChatScriptSrcSalesIq &&
        store.getters.getSalesIqWidget
      );
    },
    handler: async () => {
      const settings = store.getters.getUserLiveChatSettings;
      const salesIqScriptSrc = store.getters.getLiveChatScriptSrcSalesIq;
      const salesIqWidgetCode = store.getters.getSalesIqWidget;

      includeScript(
        salesIqScriptSrc,
        {
          type: "text/javascript",
          id: "zsiqscript",
          defer: "true",
        },
        () => {
          if (!window.$zoho) {
            window.$zoho = {
              salesiq: {
                widgetcode: salesIqWidgetCode,
                values: {},
                ready: async function () {
                  console.log("SALESIQ READY", settings);
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  this.chat.waittime(60);
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  this.visitor.email(settings.email);
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  this.visitor.name(settings.name);

                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  this.visitor.info({
                    company_id: settings.company_id,
                    token: settings.token,
                    user_id: settings.user_id,
                  });
                  if (!settings.user_id) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.floatbutton.visible("hide");
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.floatwindow.visible("hide");
                  } else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.floatbutton.visible("show");
                  }
                },
                afterReady: async function () {
                  console.log("SALESIQ AFTER READY", settings);
                  if (settings.user_id) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    this.floatbutton.visible("show");
                  }
                },
              },
            };

            return;
          }

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.$zoho.salesiq.reset();
          if (settings.user_id) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.$zoho.salesiq?.floatbutton?.visible("show");
          } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.$zoho.salesiq?.floatbutton?.visible("hide");
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.$zoho.salesiq?.floatwindow?.visible("hide");
          }
        }
      );
    },
  },
  // {
  //   name: "INCLUDE_ROCKET_CHAT_SCRIPT",
  //   namespace: "common",
  //   position: BootstrapPosition.BEFORE_ROUTE,
  //   isAllowed: () => {
  //     if (document.getElementById("rocket-support")) {
  //       return false;
  //     }
  //
  //     return (
  //       LiveChatType.ROCKET === store.getters.getLiveChatType &&
  //       isUserAuthenticated() &&
  //       store.getters.getUserLiveChatSettings &&
  //       store.getters.getLiveChatScriptSrcRocket &&
  //       store.getters.getLiveChatScriptRocketUrl
  //     );
  //   },
  //   handler: async () => {
  //     const rocketScriptSrc = store.getters.getLiveChatScriptSrcRocket;
  //     const rocketScriptUrl = store.getters.getLiveChatScriptRocketUrl;
  //     const settings = store.getters.getUserLiveChatSettings;
  //
  //     includeScript(
  //       rocketScriptSrc,
  //       {
  //         type: "text/javascript",
  //         async: "true",
  //         id: "rocket-support",
  //       },
  //       () => {
  //         window.RocketChat = function (c) {
  //           // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //           // @ts-ignore
  //           window.RocketChat?._.push(c);
  //         };
  //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //         // @ts-ignore
  //         window.RocketChat._ = [];
  //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //         // @ts-ignore
  //         window.RocketChat.url = rocketScriptUrl;
  //       },
  //       () => {
  //         // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //         // @ts-ignore
  //         window.RocketChat(function () {
  //           // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //           // @ts-ignore
  //           this.registerGuest({
  //             token: settings.token,
  //             name: settings.name,
  //             email: settings.email,
  //             customFields: [
  //               {
  //                 key: "company_id",
  //                 //convert to string
  //                 value: String(settings.company_id),
  //                 overwrite: true,
  //               },
  //               {
  //                 key: "user_id",
  //                 value: String(settings.user_id),
  //                 overwrite: true,
  //               },
  //             ],
  //           });
  //           // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //           // @ts-ignore
  //           this.showWidget();
  //         });
  //       }
  //     );
  //   },
  // },
  // {
  //   name: "REMOVE_ROCKET_CHAT_SCRIPT",
  //   namespace: "common",
  //   position: BootstrapPosition.BEFORE_ROUTE,
  //   isAllowed: () => {
  //     if (!document.getElementById("rocket-support")) {
  //       return false;
  //     }
  //
  //     return (
  //       LiveChatType.ROCKET === store.getters.getLiveChatType &&
  //       !isUserAuthenticated()
  //     );
  //   },
  //   handler: async () => {
  //     const rocketChatScript = document.getElementById("rocket-support");
  //     if (!rocketChatScript) {
  //       return;
  //     }
  //
  //     // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //     // @ts-ignore
  //     window.RocketChat(function () {
  //       // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //       // @ts-ignore
  //       this.hideWidget();
  //     });
  //
  //     rocketChatScript.remove();
  //   },
  // },
];

export const BootstrapDataConfig: BootstrapDataClause[] = [
  {
    name: "user",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_AUTH_REFRESH_USER,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (params: BootstrapParams): boolean => {
      const routes = [
        "profile.verifications",
        "manager.profile",
        "admin.profile",
      ];

      return (
        isUserAuthenticated() && routes.includes(params.route.name as string)
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_AUTH_REFRESH_USER);
    },
  },
  {
    name: "countries",
    namespace: "common",
    ttlSeconds: TtlSeconds.DAY,
    action: Actions.COMMON_DICTIONARY_COUNTRIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() && isCompanyEnabled() && isIndividualOrHas2FA()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_COUNTRIES_LOAD);
    },
  },
  {
    name: "company_alerts",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.MANAGER_COMPANY_ALERTS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isCompanyEnabled();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_ALERTS_LOAD);
    },
  },
  {
    name: "currencies",
    namespace: "common",
    ttlSeconds: TtlSeconds.DAY,
    action: Actions.COMMON_DICTIONARY_CURRENCIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() && isCompanyEnabled() && isIndividualOrHas2FA()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_CURRENCIES_LOAD);
    },
  },
  {
    name: "crypto_networks",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_CRYPTO_NETWORKS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && companyCryptoEnabled();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_CRYPTO_NETWORKS_LOAD);
    },
  },
  {
    name: "crypto_wallets",
    namespace: "common",
    ttlSeconds: TtlSeconds.MINUTE,
    action: Actions.MANAGER_COMPANY_WALLETS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        companyCryptoEnabled()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_WALLETS_LOAD);
    },
  },
  {
    name: "portals",
    namespace: "common",
    ttlSeconds: TtlSeconds.DAY,
    action: Actions.COMMON_DICTIONARY_PORTAL_SERVICES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() && isCompanyEnabled() && isIndividualOrHas2FA()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_PORTAL_SERVICES_LOAD);
    },
  },
  {
    name: "activity_logs",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_ACTIVITY_LOGS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() && isCompanyEnabled() && isIndividualOrHas2FA()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_ACTIVITY_LOGS_LOAD);
    },
  },
  {
    name: "white_label",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.MANAGER_COMPANY_WHITE_LABEL_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isCompanyWhiteLabelEnabled()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_WHITE_LABEL_LOAD);
    },
  },
  {
    name: "white_label.documents",
    namespace: "common",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.MANAGER_COMPANY_WHITE_LABEL_DOCUMENTS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isCompanyWhiteLabelEnabled()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_WHITE_LABEL_DOCUMENTS_LOAD);
    },
  },
  {
    name: "accounts",
    namespace: "manager",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.MANAGER_COMPANY_ACCOUNTS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD);
    },
  },
  {
    name: "notifications",
    namespace: "manager",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_NOTIFICATIONS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_NOTIFICATIONS_LOAD);
    },
  },
  {
    name: "announcements",
    namespace: "manager",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.MANAGER_ANNOUNCEMENTS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager() &&
        store.getters.announcementsShouldBePulled
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_ANNOUNCEMENTS_LOAD);
    },
  },
  {
    name: "affiliated_companies",
    namespace: "manager",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.MANAGER_AFFILIATE_COMPANIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager() &&
        hasAffiliates()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_AFFILIATE_COMPANIES_LOAD);
    },
  },
  {
    name: "white_labeled_companies",
    namespace: "manager",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.MANAGER_WHITE_LABELED_COMPANIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager() &&
        isCompanyWhiteLabelEnabled()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_WHITE_LABELED_COMPANIES_LOAD);
    },
  },
  {
    name: "company_statistics",
    namespace: "manager",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.MANAGER_COMPANY_STATISTIC_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return (
        isUserAuthenticated() &&
        isCompanyEnabled() &&
        isIndividualOrHas2FA() &&
        isManager()
      );
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.MANAGER_COMPANY_STATISTIC_LOAD);
    },
  },
  {
    name: "transaction_statistics",
    namespace: "admin",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.ADMIN_TRANSACTION_STATISTIC_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.ADMIN_TRANSACTION_STATISTIC_LOAD);
    },
  },
  {
    name: "timeline_entity_classes",
    namespace: "admin",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.ADMIN_TIMELINE_ENTITY_CLASSES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.ADMIN_TIMELINE_ENTITY_CLASSES_LOAD);
    },
  },
  {
    name: "settings",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.ADMIN_SETTINGS_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.ADMIN_SETTINGS_LOAD);
    },
  },
  {
    name: "company_features",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_COMPANY_FEATURES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_COMPANY_FEATURES_LOAD);
    },
  },
  {
    name: "company_industries",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_COMPANY_INDUSTRIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_COMPANY_INDUSTRIES_LOAD);
    },
  },
  {
    name: "onboarding_stages",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_ONBOARDING_STAGES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_ONBOARDING_STAGES_LOAD);
    },
  },
  {
    name: "user_document_types",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_USER_DOCUMENT_TYPES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_USER_DOCUMENT_TYPES_LOAD);
    },
  },
  {
    name: "employee_business_roles",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_EMPLOYEE_BUSINESS_ROLES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(
        Actions.COMMON_DICTIONARY_EMPLOYEE_BUSINESS_ROLES_LOAD
      );
    },
  },
  {
    name: "user_roles",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_USER_ROLES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_USER_ROLES_LOAD);
    },
  },
  {
    name: "payment_service_statuses",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICE_STATUSES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(
        Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICE_STATUSES_LOAD
      );
    },
  },
  {
    name: "corporation_types",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_CORPORATION_TYPES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.COMMON_DICTIONARY_CORPORATION_TYPES_LOAD);
    },
  },
  {
    name: "company_payment_services",
    namespace: "admin",
    ttlSeconds: TtlSeconds.FIVE_MINUTES,
    action: Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(
        Actions.COMMON_DICTIONARY_COMPANY_PAYMENT_SERVICES_LOAD
      );
    },
  },
  {
    name: "user_statistics",
    namespace: "admin",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.ADMIN_USER_STATISTIC_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.ADMIN_USER_STATISTIC_LOAD);
    },
  },
  {
    name: "companies",
    namespace: "admin",
    ttlSeconds: TtlSeconds.TEN_MINUTES,
    action: Actions.ADMIN_COMPANIES_LOAD,
    position: BootstrapPosition.BEFORE_ROUTE,
    isAllowed: (): boolean => {
      return isUserAuthenticated() && isAdmin();
    },
    handler: (): Promise<PromiseResult> => {
      return store.dispatch(Actions.ADMIN_COMPANIES_LOAD);
    },
  },
];
